import { CERTIFICATE_PREVIEW_LABELS } from '../../constants/labels';
import type { CertificateTemplateSelectedFormField } from '../../types';

const DetailCard = ({
  children,
  fixedHeight,
}: {
  children: React.ReactNode;
  fixedHeight?: number;
}) => (
  <div
    className="w-full rounded-[10px] border-l-4 border-[#2A4455] p-6 overflow-hidden"
    style={{
      background:
        'linear-gradient(to right, rgba(232, 238, 240, 0.44) 0%, rgba(209, 220, 225, 0.07) 80%)',
      ...(fixedHeight !== undefined ? { height: fixedHeight } : {}),
    }}
  >
    <div className="grid grid-cols-2 gap-x-16 gap-y-5 w-full">{children}</div>
  </div>
);

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <p className="min-w-0 text-[14px] leading-5 tracking-[0.25px] text-[#474A4C] wrap-break-word">
    {label}: <span className="text-[#181A1B]">{value}</span>
  </p>
);

// ─── CertificateIntro ─────────────────────────────────────────────────────────

interface CertificateIntroProps {
  headerText: string;
  subtitleText: string;
  /** When provided, replaces the placeholder client name. */
  clientName?: string;
  /** When provided, appended as "(ID)" after the client name. */
  clientBusinessId?: string;
  /** When provided, replaces the placeholder client email. */
  clientEmail?: string;
  /** When provided, replaces the placeholder client phone. */
  clientPhone?: string | null;
  /** When provided, replaces the placeholder facility name. */
  facilityName?: string;
  /** When provided, appended as "(ID)" after the facility name. */
  facilityBusinessId?: string;
  /** When provided, replaces the placeholder facility address. */
  facilityAddress?: string;
  /** When provided, replaces the placeholder date of issue. */
  dateOfIssue?: string;
}

/** Header + subtitle + client/facility block (the top of the certificate body). */
export const CertificateIntro = ({
  headerText,
  subtitleText,
  clientName,
  clientBusinessId,
  clientEmail,
  clientPhone,
  facilityName,
  facilityBusinessId,
  facilityAddress,
  dateOfIssue,
}: CertificateIntroProps) => {
  const displayClientName = clientName
    ? clientBusinessId
      ? `${clientName} (${clientBusinessId})`
      : clientName
    : CERTIFICATE_PREVIEW_LABELS.CLIENT_NAME_PLACEHOLDER;

  const displayFacilityName = facilityName
    ? facilityBusinessId
      ? `${facilityName} (${facilityBusinessId})`
      : facilityName
    : CERTIFICATE_PREVIEW_LABELS.FACILITY_NAME_PLACEHOLDER;

  return (
    <div className="flex flex-col gap-12 w-full">
      {/* Header + subtitle */}
      <div className="flex flex-col gap-6 items-center w-full">
        <div className="flex flex-col gap-2 items-center w-full">
          <p className="font-roboto font-medium text-[36px] leading-11 text-center text-[#181A1B] break-all w-full">
            {headerText}
          </p>
          <div
            className="h-1 w-64 rounded-full"
            style={{
              background:
                'linear-gradient(to right, rgba(250, 212, 25, 0.2) 0%, #FFCC00 50%, rgba(250, 212, 25, 0.2) 100%)',
            }}
          />
        </div>
        <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-center text-[#474A4C] w-full wrap-break-word">
          {subtitleText}
        </p>
      </div>

      {/* 2-column grid: row1 = client | facility, row2 = empty | date of issue */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-2 w-full">
        {/* row 1 col 1: client */}
        <div className="flex flex-col min-w-0">
          <p className="font-roboto font-medium text-[14px] leading-5 tracking-[0.25px] text-[#181A1B] wrap-break-word">
            {displayClientName}
          </p>
          <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-[#474A4C] whitespace-pre-line wrap-break-word">
            {clientEmail ?? CERTIFICATE_PREVIEW_LABELS.CLIENT_EMAIL_PLACEHOLDER}
            {'\n'}
            {clientPhone ?? CERTIFICATE_PREVIEW_LABELS.CLIENT_PHONE_PLACEHOLDER}
          </p>
        </div>
        {/* row 1 col 2: facility */}
        <div className="flex flex-col min-w-0">
          <p className="font-roboto font-medium text-[14px] leading-5 tracking-[0.25px] text-[#181A1B] wrap-break-word">
            {displayFacilityName}
          </p>
          <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-[#474A4C] whitespace-pre-line wrap-break-word">
            {facilityAddress ??
              CERTIFICATE_PREVIEW_LABELS.FACILITY_ADDRESS_PLACEHOLDER}
          </p>
        </div>
        {/* row 2 col 1: empty */}
        <div />
        {/* row 2 col 2: date of issue */}
        <div className="flex flex-col min-w-0">
          <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-[#474A4C]">
            {CERTIFICATE_PREVIEW_LABELS.DATE_OF_ISSUE_LABEL}:{' '}
            {dateOfIssue ??
              CERTIFICATE_PREVIEW_LABELS.DATE_OF_ISSUE_PLACEHOLDER}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── EquipmentSectionTitle ────────────────────────────────────────────────────

/** Centered "Equipment data" section heading. */
export const EquipmentSectionTitle = () => (
  <div className="w-full text-center">
    <p className="font-roboto font-medium text-[22px] leading-7 text-[#181A1B]">
      {CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_SECTION_TITLE}
    </p>
  </div>
);

// ─── EquipmentDetailCard ──────────────────────────────────────────────────────

interface EquipmentDetailCardProps {
  fixedHeight?: number;
  /** When provided, populates real equipment values instead of placeholders. */
  equipmentName?: string | null;
  equipmentId?: string | null;
  equipmentModel?: string | null;
  equipmentSerial?: string | null;
  productionDate?: string | null;
  equipmentType?: string | null;
  serviceTypeName?: string | null;
}

/** Equipment detail card — placeholder values by default, real data when props supplied. */
export const EquipmentDetailCard = ({
  fixedHeight,
  equipmentName,
  equipmentId,
  equipmentModel,
  equipmentSerial,
  productionDate,
  equipmentType,
  serviceTypeName,
}: EquipmentDetailCardProps = {}) => {
  // undefined → template preview (no real data), show bracket placeholder
  // null      → real data missing, show em-dash
  // string    → real value, show as-is
  const val = (label: string, override?: string | null) => {
    if (override === undefined) {
      return CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_VALUE_PLACEHOLDER(label);
    }
    return override ?? '—';
  };

  return (
    <DetailCard {...(fixedHeight !== undefined ? { fixedHeight } : {})}>
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_NAME}
        value={val(
          CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_NAME,
          equipmentName
        )}
      />
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_ID}
        value={val(CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_ID, equipmentId)}
      />
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_MODEL}
        value={val(
          CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_MODEL,
          equipmentModel
        )}
      />
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_SERIAL}
        value={val(
          CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_SERIAL,
          equipmentSerial
        )}
      />
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_PRODUCTION_DATE}
        value={val(
          CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_PRODUCTION_DATE,
          productionDate
        )}
      />
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_TYPE}
        value={val(
          CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_TYPE,
          equipmentType
        )}
      />
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_SERVICE_TYPE}
        value={val(
          CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_SERVICE_TYPE,
          serviceTypeName
        )}
      />
    </DetailCard>
  );
};

// ─── FormFieldsCard ───────────────────────────────────────────────────────────

interface FormFieldsCardProps {
  fields: CertificateTemplateSelectedFormField[];
  fixedHeight?: number;
  /**
   * When provided, looks up each field's actual value by field ID.
   * Falls back to the placeholder when a field ID is not found in the map.
   */
  fieldValues?: Record<string, string>;
}

/** A detail card rendering up to 6 selected form fields as subfields. */
export const FormFieldsCard = ({
  fields,
  fixedHeight,
  fieldValues,
}: FormFieldsCardProps) => (
  <DetailCard {...(fixedHeight !== undefined ? { fixedHeight } : {})}>
    {fields.map((field) => (
      <DetailRow
        key={field.id}
        label={field.label}
        value={
          fieldValues?.[field.id] ??
          CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_VALUE_PLACEHOLDER(field.label)
        }
      />
    ))}
  </DetailCard>
);

// ─── ServiceRequestDetailCard ─────────────────────────────────────────────────

/** Centered "Service Request Details" section heading. */
export const ServiceRequestSectionTitle = () => (
  <div className="w-full text-center">
    <p className="font-roboto font-medium text-[22px] leading-7 text-[#181A1B]">
      {CERTIFICATE_PREVIEW_LABELS.SR_SECTION_TITLE}
    </p>
  </div>
);

interface ServiceRequestDetailCardProps {
  fixedHeight?: number;
  serviceRequestId?: string | null;
  serviceTypeName?: string | null;
}

/** Service request detail card — SR ID and service type. */
export const ServiceRequestDetailCard = ({
  fixedHeight,
  serviceRequestId,
  serviceTypeName,
}: ServiceRequestDetailCardProps = {}) => {
  const val = (label: string, override?: string | null) => {
    if (override === undefined) {
      return CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_VALUE_PLACEHOLDER(label);
    }
    return override ?? '—';
  };
  return (
    <DetailCard {...(fixedHeight !== undefined ? { fixedHeight } : {})}>
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.SR_FIELD_ID}
        value={val(CERTIFICATE_PREVIEW_LABELS.SR_FIELD_ID, serviceRequestId)}
      />
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.SR_FIELD_TYPE}
        value={val(CERTIFICATE_PREVIEW_LABELS.SR_FIELD_TYPE, serviceTypeName)}
      />
    </DetailCard>
  );
};

// ─── FormDetailCard ───────────────────────────────────────────────────────────

/** Centered "Form Details" section heading. */
export const FormSectionTitle = () => (
  <div className="w-full text-center">
    <p className="font-roboto font-medium text-[22px] leading-7 text-[#181A1B]">
      {CERTIFICATE_PREVIEW_LABELS.FORM_SECTION_TITLE}
    </p>
  </div>
);

interface FormDetailCardProps {
  fixedHeight?: number;
  formSubmissionId?: string | null;
  formTemplateName?: string | null;
  /** Selected form fields — rendered after the form metadata rows. */
  fields?: CertificateTemplateSelectedFormField[];
  /** Actual field values keyed by field ID. */
  fieldValues?: Record<string, string>;
}

/** Form metadata (ID + name) followed by all selected form field values. */
export const FormDetailCard = ({
  fixedHeight,
  formSubmissionId,
  formTemplateName,
  fields = [],
  fieldValues,
}: FormDetailCardProps = {}) => {
  const val = (label: string, override?: string | null) => {
    if (override === undefined) {
      return CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_VALUE_PLACEHOLDER(label);
    }
    return override ?? '—';
  };
  return (
    <DetailCard {...(fixedHeight !== undefined ? { fixedHeight } : {})}>
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.SR_FIELD_FORM_ID}
        value={val(
          CERTIFICATE_PREVIEW_LABELS.SR_FIELD_FORM_ID,
          formSubmissionId
        )}
      />
      <DetailRow
        label={CERTIFICATE_PREVIEW_LABELS.SR_FIELD_FORM_NAME}
        value={val(
          CERTIFICATE_PREVIEW_LABELS.SR_FIELD_FORM_NAME,
          formTemplateName
        )}
      />
      {fields.map((field) => (
        <DetailRow
          key={field.id}
          label={field.label}
          value={
            fieldValues?.[field.id] ??
            CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_VALUE_PLACEHOLDER(field.label)
          }
        />
      ))}
    </DetailCard>
  );
};

// ─── CertificateContent ───────────────────────────────────────────────────────

interface CertificateContentProps {
  headerText: string;
  subtitleText: string;
  formFields: CertificateTemplateSelectedFormField[];
}

/**
 * The full certificate body, composed from its block pieces. Used directly for
 * a single-page render; {@link CertificatePreview} composes the same pieces
 * across pages when the content overflows a single A4 sheet.
 */
export const CertificateContent = ({
  headerText,
  subtitleText,
  formFields,
}: CertificateContentProps) => (
  <div className="flex flex-col gap-10">
    <CertificateIntro headerText={headerText} subtitleText={subtitleText} />
    <EquipmentSectionTitle />
    <div className="flex flex-col gap-8 w-full">
      <EquipmentDetailCard />
      {formFields.length > 0 && <FormFieldsCard fields={formFields} />}
    </div>
  </div>
);
